import React, { useEffect, useRef } from 'react';
import { Calendar, Award, Users, Globe, Building, Truck, Wrench, Factory, Target, Rocket } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface Milestone {
  year: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
  isHighlight?: boolean;
}

// Desktop Timeline Item Component
const DesktopTimelineItem: React.FC<Milestone> = ({
  year,
  icon,
  title,
  description,
  index,
  isHighlight = false
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);
  const isLeft = index % 2 === 0;

  useEffect(() => {
    const element = itemRef.current;
    const iconElement = iconRef.current;
    const yearElement = yearRef.current;

    if (!element) return;

    // Initial states
    gsap.set(element, {
      opacity: 0,
      x: isLeft ? -100 : 100,
      y: 50,
      scale: 0.8
    });

    gsap.set(iconElement, {
      scale: 0,
      rotation: -180
    });

    gsap.set(yearElement, {
      scale: 0,
      rotation: 360
    });

    // Scroll-triggered animations
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: element,
        start: "top 85%",
        end: "bottom 15%",
        toggleActions: "play none none reverse"
      }
    });

    // Animate the main element
    tl.to(element, {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      duration: 0.8,
      ease: "back.out(1.7)",
      delay: index * 0.1
    });

    // Animate the icon
    tl.to(iconElement, {
      scale: 1,
      rotation: 0,
      duration: 0.6,
      ease: "back.out(1.7)"
    }, "-=0.4");

    // Animate the year badge
    tl.to(yearElement, {
      scale: 1,
      rotation: 0,
      duration: 0.6,
      ease: "back.out(1.7)"
    }, "-=0.3");

    // Add hover animations
    const handleMouseEnter = () => {
      gsap.to(element, {
        scale: 1.05,
        y: -10,
        duration: 0.3,
        ease: "power2.out"
      });

      gsap.to(iconElement, {
        scale: 1.2,
        rotation: 15,
        duration: 0.3,
        ease: "power2.out"
      });

      gsap.to(yearElement, {
        scale: 1.1,
        duration: 0.3,
        ease: "power2.out"
      });
    };

    const handleMouseLeave = () => {
      gsap.to(element, {
        scale: 1,
        y: 0,
        duration: 0.3,
        ease: "power2.out"
      });

      gsap.to(iconElement, {
        scale: 1,
        rotation: 0,
        duration: 0.3,
        ease: "power2.out"
      });

      gsap.to(yearElement, {
        scale: 1,
        duration: 0.3,
        ease: "power2.out"
      });
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [index, isLeft]);

  return (
    <div
      ref={itemRef}
      className={`relative hidden md:flex items-center ${isLeft ? 'flex-row' : 'flex-row-reverse'
        }`}
    >
      {/* Timeline Content */}
      <div className={`w-full flex items-center ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
        {/* Content Card */}
        <div className={`w-5/12 ${isLeft ? 'pr-8' : 'pl-8'}`}>
          <div className={`relative p-6 rounded-2xl shadow-lg cursor-pointer transform-gpu ${isHighlight
            ? 'bg-gradient-to-br from-[#CC0000] to-[#AA0000] text-white'
            : 'bg-white border border-gray-100'
            }`}>
            {/* Arrow pointing to timeline */}
            <div className={`absolute top-1/2 -translate-y-1/2 w-0 h-0 ${isLeft
              ? 'right-0 translate-x-full border-l-8 border-y-8 border-y-transparent'
              : 'left-0 -translate-x-full border-r-8 border-y-8 border-y-transparent'
              } ${isHighlight
                ? 'border-l-[#CC0000] border-r-[#CC0000]'
                : 'border-l-white border-r-white'
              }`}></div>

            <div className="flex items-start space-x-4">
              <div
                ref={iconRef}
                className={`p-3 rounded-xl ${isHighlight
                  ? 'bg-white/20'
                  : 'bg-[#CC0000]/10'
                  }`}
              >
                <div className={isHighlight ? 'text-white' : 'text-[#CC0000]'}>
                  {icon}
                </div>
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-bold mb-2 ${isHighlight ? 'text-white' : 'text-[#1A1A1A]'
                  }`}>
                  {title}
                </h3>
                <p className={`text-sm leading-relaxed ${isHighlight ? 'text-white/90' : 'text-gray-600'
                  }`}>
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Center Point */}
        <div className="w-2/12 flex justify-center">
          <div className="relative">
            {/* Year Badge */}
            <div
              ref={yearRef}
              className={`relative z-10 w-20 h-20 rounded-full flex items-center justify-center font-bold text-sm ${isHighlight
                ? 'bg-[#CC0000] text-white shadow-lg'
                : 'bg-white text-[#CC0000] border-4 border-[#CC0000] shadow-md'
                }`}
            >
              {year}
            </div>

            {/* Connecting Line */}
            {index < 14 && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-1 h-16 bg-gradient-to-b from-[#CC0000] to-[#CC0000]/30"></div>
            )}
          </div>
        </div>

        {/* Empty space for opposite side */}
        <div className="w-5/12"></div>
      </div>
    </div>
  );
};

// Mobile Timeline Item Component
const MobileTimelineItem: React.FC<Milestone> = ({
  year,
  icon,
  title,
  description,
  index,
  isHighlight = false
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = itemRef.current;
    const iconElement = iconRef.current;
    const yearElement = yearRef.current;

    if (!element) return;

    // Initial states
    gsap.set(element, {
      opacity: 0,
      y: 30,
      scale: 0.95
    });

    gsap.set(iconElement, {
      scale: 0,
      rotation: -180
    });

    gsap.set(yearElement, {
      scale: 0,
      rotation: 360
    });

    // Scroll-triggered animations
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: element,
        start: "top 85%",
        end: "bottom 15%",
        toggleActions: "play none none reverse"
      }
    });

    // Animate the main element
    tl.to(element, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      ease: "back.out(1.7)",
      delay: index * 0.1
    });

    // Animate the icon
    tl.to(iconElement, {
      scale: 1,
      rotation: 0,
      duration: 0.6,
      ease: "back.out(1.7)"
    }, "-=0.4");

    // Animate the year badge
    tl.to(yearElement, {
      scale: 1,
      rotation: 0,
      duration: 0.6,
      ease: "back.out(1.7)"
    }, "-=0.3");
  }, [index]);

  return (
    <div ref={itemRef} className="md:hidden block">
      <div className={`relative p-6 rounded-2xl shadow-lg mb-8 ${isHighlight
        ? 'bg-gradient-to-br from-[#CC0000] to-[#AA0000] text-white'
        : 'bg-white border border-gray-100'
        }`}>
        {/* Year Badge */}
        <div
          ref={yearRef}
          className={`absolute -top-4 left-1/2 -translate-x-1/2 z-10 w-16 h-16 rounded-full flex items-center justify-center font-bold text-sm ${isHighlight
            ? 'bg-white text-[#CC0000] shadow-lg'
            : 'bg-[#CC0000] text-white shadow-md'
            }`}
        >
          {year}
        </div>

        {/* Content */}
        <div className="pt-8">
          <div className="flex items-start space-x-4">
            <div
              ref={iconRef}
              className={`p-3 rounded-xl ${isHighlight
                ? 'bg-white/20'
                : 'bg-[#CC0000]/10'
                }`}
            >
              <div className={isHighlight ? 'text-white' : 'text-[#CC0000]'}>
                {icon}
              </div>
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-bold mb-2 ${isHighlight ? 'text-white' : 'text-[#1A1A1A]'
                }`}>
                {title}
              </h3>
              <p className={`text-sm leading-relaxed ${isHighlight ? 'text-white/90' : 'text-gray-600'
                }`}>
                {description}
              </p>
            </div>
          </div>
        </div>

        {/* Connecting Line */}
        {index < 14 && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-8 bg-gradient-to-b from-[#CC0000] to-[#CC0000]/30"></div>
        )}
      </div>
    </div>
  );
};

// Main Timeline Item Component
const TimelineItem: React.FC<Milestone> = (props) => {
  return (
    <>
      <DesktopTimelineItem {...props} />
      <MobileTimelineItem {...props} />
    </>
  );
};

const History: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const stats = statsRef.current;

    if (!section || !title || !subtitle || !stats) return;

    // Initial states
    gsap.set([title, subtitle], {
      opacity: 0,
      y: 50
    });

    gsap.set(stats, {
      opacity: 0,
      y: 100,
      scale: 0.9
    });

    // Title animations
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse"
      }
    });

    tl.to(title, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: "back.out(1.7)"
    })
      .to(subtitle, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out"
      }, "-=0.5");

    // Stats animation
    gsap.to(stats, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 1,
      ease: "back.out(1.7)",
      scrollTrigger: {
        trigger: stats,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse"
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const milestones: Milestone[] = [
    {
      year: "2011",
      icon: <Globe size={20} />,
      title: "Our First Global Footprint",
      description: "Received our first international order from Proton Car Company via DreamEdge Malaysia – a defining start to our global journey.",
      index: 0
    },
    {
      year: "2012",
      icon: <Award size={20} />,
      title: "Engineering Excellence Recognized",
      description: "Commissioned by Mercedes-Benz to benchmark a BMW engine. Developed our first complete autorickshaw from design to a fully functional prototype.",
      index: 1
    },
    {
      year: "2013",
      icon: <Building size={20} />,
      title: "Designs That Travel the World",
      description: "Delivered all composite molds for the Sydney Metro (interior and exterior) for Alstom through MSL.",
      index: 2
    },
    {
      year: "2014",
      icon: <Globe size={20} />,
      title: "European Expansion",
      description: "Started onsite and offsite collaboration with an Italian automotive firm, extending our reach into Europe.",
      index: 3
    },
    {
      year: "2015",
      icon: <Factory size={20} />,
      title: "Infrastructure for Innovation",
      description: "Established a full-fledged 20,000 sq. ft. design-to-prototype development facility.",
      index: 4
    },
    {
      year: "2016",
      icon: <Truck size={20} />,
      title: "Production Milestone",
      description: "Successfully delivered over 1,000 vehicles for various clients across automotive segments.",
      index: 5
    },
    {
      year: "2017",
      icon: <Wrench size={20} />,
      title: "Smarter Manufacturing Begins",
      description: "Initiated work on low-volume tooling and custom production techniques, paving the way for flexible manufacturing.",
      index: 6
    },
    {
      year: "2018",
      icon: <Award size={20} />,
      title: "Working with Icons",
      description: "Received a project from Aston Martin via a British design consultancy. Set up a branch office for the UK firm, later merging its operations into 3D Magic.",
      index: 7
    },
    {
      year: "2019",
      icon: <Target size={20} />,
      title: "Scaling Innovation",
      description: "Delivered 40 testable autorickshaw prototypes for Atul Auto, from concept to working models.",
      index: 8
    },
    {
      year: "2020",
      icon: <Users size={20} />,
      title: "Growth in Demand",
      description: "Secured large orders to supply tooling and vehicle bodies for over five companies covering 3- and 4-wheelers.",
      index: 9
    },
    {
      year: "2021",
      icon: <Building size={20} />,
      title: "Investing in Scale",
      description: "Announced a ₹100 Crore investment to build a facility with a 5,000 vehicles per month manufacturing capacity.",
      index: 10,
      isHighlight: true
    },
    {
      year: "2022",
      icon: <Factory size={20} />,
      title: "Laying a Strong Foundation",
      description: "Acquired land for a new facility. Built a plant with a capacity of 3,000 plus vehicles per month. Began prototyping for 4-wheeler platforms.",
      index: 11
    },
    {
      year: "2023",
      icon: <Truck size={20} />,
      title: "Record Deliveries and Expansion",
      description: "Delivered over 10,000 vehicles across multiple categories. Continued infrastructure growth to support large-scale production.",
      index: 12
    },
    {
      year: "2024",
      icon: <Rocket size={20} />,
      title: "Launched Our Own OEM Dawki Mobility",
      description: "Proudly introduced Dawki Mobility, our in-house Original Equipment Manufacturer brand. Began production of our own vehicles under the Dawki name, marking a major step in becoming a full-stack mobility company.",
      index: 13,
      isHighlight: true
    },
    {
      year: "2025+",
      icon: <Globe size={20} />,
      title: "Driving the Future",
      description: "Committed to building smarter, cleaner, and more accessible vehicles backed by deep design expertise, world-class infrastructure, and digital innovation.",
      index: 14,
      isHighlight: true
    }
  ];

  return (
    <section ref={sectionRef} id="history-section" className="py-20 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern - Modified for better mobile visibility */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #CC0000 2px, transparent 2px)`,
            backgroundSize: '50px 50px'
          }}
        ></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-20">
          <div className="inline-block bg-[#CC0000]/10 text-[#CC0000] px-6 py-2 rounded-full text-sm font-semibold mb-6">
            Our Legacy
          </div>
          <h2 ref={titleRef} className="text-5xl md:text-6xl font-bold text-[#1A1A1A] mb-6">
            Journey of
            <span className="block text-[#CC0000]">Innovation</span>
          </h2>
          <p ref={subtitleRef} className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From our first international order to becoming a full-stack mobility company,
            witness the milestones that shaped our path to automotive excellence.
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-6xl mx-auto">
          <div className="space-y-8 md:space-y-8">
            {milestones.map((milestone) => (
              <TimelineItem
                key={milestone.index}
                {...milestone}
              />
            ))}
          </div>
        </div>

        {/* Achievement Stats */}
        <div ref={statsRef} className="mt-20 bg-gradient-to-r from-[#CC0000] to-[#AA0000] rounded-3xl p-12 text-white">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">Our Impact Today</h3>
            <p className="text-white/90 max-w-2xl mx-auto">
              Numbers that reflect our commitment to excellence and innovation in the automotive industry.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">14+</div>
              <div className="text-white/80 text-sm uppercase tracking-wider">Years of Excellence</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">10K+</div>
              <div className="text-white/80 text-sm uppercase tracking-wider">Vehicles Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">₹100Cr</div>
              <div className="text-white/80 text-sm uppercase tracking-wider">Investment Milestone</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold mb-2">Global</div>
              <div className="text-white/80 text-sm uppercase tracking-wider">Reach & Impact</div>
            </div>
          </div>
        </div>

        {/* Future Vision */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-3 bg-white rounded-full px-8 py-4 shadow-lg">
            <div className="w-3 h-3 bg-[#CC0000] rounded-full animate-pulse"></div>
            <span className="text-[#1A1A1A] font-semibold">The journey continues...</span>
            <div className="w-3 h-3 bg-[#CC0000] rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default History;